import { useCallback, useSyncExternalStore } from 'react';

/** UnoCSS default breakpoints (uno.config.ts doesn't override them). */
export const BREAKPOINTS = { sm: 640, md: 768, lg: 1024, xl: 1280 } as const;

/** Media query for phones/tablets — matches the `touch:` Uno variant. */
export const TOUCH_QUERY = '(hover: none), (pointer: coarse)';

/** Subscribes to a CSS media query; re-renders only when its match flips. */
export function useMediaQuery(query: string) {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const mql = window.matchMedia(query);
      mql.addEventListener('change', onChange);

      return () => mql.removeEventListener('change', onChange);
    },
    [query],
  );

  return useSyncExternalStore(
    subscribe,
    () => window.matchMedia(query).matches,
    () => false,
  );
}

/** True below `width` px — same boundary as Uno's `lt-*` / the absence of a `sm:`/`md:`/`lg:` prefix. */
export function useIsBelow(width: number) {
  return useMediaQuery(`(max-width: ${width - 0.02}px)`);
}

/** True on devices without hover or with a coarse pointer (phones, tablets). */
export function useIsTouch() {
  return useMediaQuery(TOUCH_QUERY);
}
